import React, { useState, useCallback, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import {
  FileCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Link as LinkIcon,
  Add as AddIcon,
} from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import UTMCreateModal from "../../components/UTMCreateModal";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
}));

const UTMLinkGenerator = () => {
  const classes = useStyles();

  const [utmLinks, setUtmLinks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Carregar links UTM salvos
  useEffect(() => {
    const fetchUTMLinks = async () => {
      setLoading(true);
      try {
        const response = await api.get("/utm/links");
        // Converter os dados para o formato esperado
        const links = response.data.links.map(link => ({
          phoneNumber: link.phoneNumber,
          utmSource: link.utmSource,
          utmCampaign: link.utmCampaign,
          utmMedium: link.utmMedium,
          utmTerm: link.utmTerm,
          utmContent: link.utmContent,
          // Gerar o link completo
          generatedLink: `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/w/${link.phoneNumber}?utm_source=${link.utmSource}&utm_campaign=${link.utmCampaign}${link.utmMedium ? `&utm_medium=${link.utmMedium}` : ''}${link.utmTerm ? `&utm_term=${link.utmTerm}` : ''}${link.utmContent ? `&utm_content=${link.utmContent}` : ''}`,
          createdAt: link.createdAt
        }));
        setUtmLinks(links);
      } catch (err) {
        console.error("Erro ao buscar links UTM:", err);
        toast.error(i18n.t("errors.fetchFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchUTMLinks();
  }, []);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSaveLink = async (linkData) => {
    try {
      // Após salvar, recarregar a lista de links
      const response = await api.get("/utm/links");
      // Converter os dados para o formato esperado
      const links = response.data.links.map(link => ({
        phoneNumber: link.phoneNumber,
        utmSource: link.utmSource,
        utmCampaign: link.utmCampaign,
        utmMedium: link.utmMedium,
        utmTerm: link.utmTerm,
        utmContent: link.utmContent,
        // Gerar o link completo
        generatedLink: `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/w/${link.phoneNumber}?utm_source=${link.utmSource}&utm_campaign=${link.utmCampaign}${link.utmMedium ? `&utm_medium=${link.utmMedium}` : ''}${link.utmTerm ? `&utm_term=${link.utmTerm}` : ''}${link.utmContent ? `&utm_content=${link.utmContent}` : ''}`,
        createdAt: link.createdAt
      }));
      setUtmLinks(links);
      toast.success(i18n.t("common.saveSuccess"));
    } catch (err) {
      console.error("Erro ao atualizar lista de links UTM:", err);
      toast.error(i18n.t("errors.fetchFailed"));
    }
  };

  const handleCopySavedLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success(i18n.t("utm.generator.linkCopied"));
  };

  const handleOpenSavedLink = (link) => {
    window.open(link, "_blank");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("utm.generator.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            {i18n.t("common.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          {/* Tabela de Links UTM Criados */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {i18n.t("utm.generator.createdLinks")}
            </Typography>
            
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : (
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("utm.generator.phoneNumber")}</TableCell>
                      <TableCell>{i18n.t("utm.generator.source")}</TableCell>
                      <TableCell>{i18n.t("utm.generator.campaign")}</TableCell>
                      <TableCell>{i18n.t("utm.generator.medium")}</TableCell>
                      <TableCell>{i18n.t("common.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {utmLinks.map((link, index) => (
                      <TableRow key={index}>
                        <TableCell>{link.phoneNumber}</TableCell>
                        <TableCell>
                          <Chip 
                            label={link.utmSource} 
                            size="small" 
                            className={classes.chip}
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={link.utmCampaign} 
                            size="small" 
                            className={classes.chip}
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{link.utmMedium || "-"}</TableCell>
                        <TableCell>
                          <div className={classes.actionButtons}>
                            <Tooltip title={i18n.t("common.copy")}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopySavedLink(link.generatedLink)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("utm.generator.openWhatsApp")}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenSavedLink(link.generatedLink)}
                                style={{ color: "#25D366" }}
                              >
                                <WhatsAppIcon />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {utmLinks.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {i18n.t("utm.generator.noLinks")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Paper>

      <UTMCreateModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveLink}
      />
    </MainContainer>
  );
};

export default UTMLinkGenerator;